import type { LoadSample, PairSample, ResolvedProbe, WindowSummary } from '@core/line-probe';

/** The history keeps the whole run up to this many seconds; the oldest seconds go first. */
export const MAX_SECONDS = 2 * 60 * 60;
/** A second is complete once its last probe passed the 3-second deadline (spec 4.3); the rest covers the tick spacing. */
export const SETTLE_MS = 3_500;

/** One second of one target, cut on send time like the windows (spec 4.4). */
export interface SecondBucket {
    second: number;
    sent: number;
    echoed: number;
    lostUp: number;
    lostDown: number;
    lostUnknown: number;
    /** TCP only: probes without an echo within 1 second. */
    late: number;
    rttP50: number | null;
    rttP95: number | null;
    rttMax: number | null;
    jitterP50: number | null;
    /** Mean of the server's load reports that arrived in this second; null when none did (a gap, not a zero). */
    loadSentKbps: number | null;
    loadReceivedKbps: number | null;
}

/** Seconds `[from, to)` relative to the start of the run. */
export interface Span {
    from: number;
    to: number;
}

interface Raw {
    counts: Pick<SecondBucket, 'sent' | 'echoed' | 'lostUp' | 'lostDown' | 'lostUnknown' | 'late'>;
    rtts: number[];
    ipdv: number[];
    /** Per-report rates in kbit/s: each report covers about one second. */
    loadSentKbps: number[];
    loadReceivedKbps: number[];
    cached?: SecondBucket;
    /** Set once the second settled: its samples are released and its percentiles stay as they were. */
    frozen?: Percentiles;
}

type Percentiles = Pick<SecondBucket, 'rttP50' | 'rttP95' | 'rttMax' | 'jitterP50'>;

/** Nearest-rank percentile, the same rule the windows use. */
export function percentile(values: number[], q: number): number | null {
    if (values.length === 0) return null;
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1))];
}

const round1 = (x: number | null) => (x === null ? null : Math.round(x * 10) / 10);
const nonNull = (xs: (number | null)[]) => xs.filter((x): x is number => x !== null);
const mean = (xs: number[]) => (xs.length ? Math.round(xs.reduce((n, x) => n + x, 0) / xs.length) : null);

/**
 * The probes and load reports of a run, per target and per second, for the live charts. Fed by the
 * runner's `onResolved` and `onLoadSample`; seconds count from the moment the run started.
 */
export class PerSecondSeries {
    private readonly targets = new Map<string, Map<number, Raw>>();
    /** Per target, the main-thread stalls as merged spans of seconds, oldest first. */
    private readonly stalls = new Map<string, Span[]>();
    private latest = Number.NEGATIVE_INFINITY;

    constructor(
        readonly originMono: number,
        readonly originWall: number
    ) {}

    secondOfMono(mono: number): number {
        return Math.floor((mono - this.originMono) / 1000);
    }

    /** The newest second whose probes have all resolved at `nowMono`. */
    settledThrough(nowMono: number): number {
        return Math.floor((nowMono - this.originMono - SETTLE_MS) / 1000) - 1;
    }

    /** The newest second that holds anything, or -1. */
    lastSecond(): number {
        return Number.isFinite(this.latest) ? this.latest : -1;
    }

    addResolved(target: string, probes: ResolvedProbe[], pairs: PairSample[]): void {
        for (const p of probes) {
            // A stalled probe was not judged (spec 4.3); the stall shows as a band of its own.
            if (p.outcome.kind === 'stalled') continue;
            const r = this.raw(target, this.secondOfMono(p.sendMono));
            if (!r) continue;
            r.counts.sent++;
            const o = p.outcome;
            if (o.kind === 'echoed') r.counts.echoed++;
            else if (o.kind === 'late') r.counts.late++;
            else if (o.kind === 'up') r.counts.lostUp++;
            else if (o.kind === 'down') r.counts.lostDown++;
            else r.counts.lostUnknown++;
            // A late TCP echo still has a round trip, and the windows count it too.
            if ((o.kind === 'echoed' || o.kind === 'late') && o.rttMs !== undefined && !r.frozen) r.rtts.push(o.rttMs);
            r.cached = undefined;
        }
        for (const p of pairs) {
            const r = this.raw(target, this.secondOfMono(p.atMono));
            if (!r || r.frozen) continue;
            r.ipdv.push(p.ipdvMs);
            r.cached = undefined;
        }
    }

    /**
     * A load report of the server, in the second it arrived on the probes' monotonic clock (`atMono`),
     * so a wall-clock step cannot shift it; the load runs to `target`. A report covers about one
     * second, so each one is a rate: two in one second average, none leaves the second without a value.
     */
    addLoadSample(target: string, sample: Pick<LoadSample, 'sentBytes' | 'receivedBytes'>, atMono: number): void {
        const r = this.raw(target, this.secondOfMono(atMono));
        if (!r) return;
        // bytes * 8 / 1000 ms = kbit/s
        r.loadSentKbps.push((sample.sentBytes * 8) / 1000);
        r.loadReceivedKbps.push((sample.receivedBytes * 8) / 1000);
        r.cached = undefined;
    }

    /** A main-thread stall of the page, `[fromMono, toMono]`, as reported by the runner's `onStalled`. */
    addStall(target: string, fromMono: number, toMono: number): void {
        const from = this.secondOfMono(fromMono);
        const to = this.secondOfMono(toMono) + 1;
        if (to <= this.latest - MAX_SECONDS + 1) return;
        let spans = this.stalls.get(target);
        if (!spans) {
            spans = [];
            this.stalls.set(target, spans);
        }
        const last = spans[spans.length - 1];
        if (last && last.to >= from) last.to = Math.max(last.to, to);
        else spans.push({ from, to });
    }

    /** The seconds in which the target saw a main-thread stall, as spans: the browser, not the line. */
    stalledSpans(target: string): Span[] {
        return (this.stalls.get(target) ?? []).map((s) => ({ ...s }));
    }

    /**
     * The target's seconds, oldest first. Seconds up to `settled` release their samples once
     * summarised: a two-hour run would otherwise keep every round trip.
     */
    seconds(target: string, settled = Number.NEGATIVE_INFINITY): SecondBucket[] {
        const m = this.targets.get(target);
        if (!m) return [];
        return [...m.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([second, r]) => {
                r.cached ??= summarise(second, r);
                if (second <= settled && !r.frozen) {
                    const { rttP50, rttP95, rttMax, jitterP50 } = r.cached;
                    r.frozen = { rttP50, rttP95, rttMax, jitterP50 };
                    r.rtts = [];
                    r.ipdv = [];
                }
                return r.cached;
            });
    }

    private raw(target: string, second: number): Raw | undefined {
        if (second < this.latest - MAX_SECONDS + 1) return undefined;
        if (second > this.latest) {
            this.latest = second;
            const oldest = second - MAX_SECONDS + 1;
            for (const m of this.targets.values()) for (const s of m.keys()) if (s < oldest) m.delete(s);
            for (const spans of this.stalls.values()) while (spans.length && spans[0].to <= oldest) spans.shift();
        }
        let m = this.targets.get(target);
        if (!m) {
            m = new Map();
            this.targets.set(target, m);
        }
        let r = m.get(second);
        if (!r) {
            r = {
                counts: { sent: 0, echoed: 0, lostUp: 0, lostDown: 0, lostUnknown: 0, late: 0 },
                rtts: [],
                ipdv: [],
                loadSentKbps: [],
                loadReceivedKbps: []
            };
            m.set(second, r);
        }
        return r;
    }
}

function summarise(second: number, r: Raw): SecondBucket {
    // A straggler for a settled second only adds to its counts: its samples are gone.
    const percentiles: Percentiles = r.frozen ?? {
        rttP50: round1(percentile(r.rtts, 0.5)),
        rttP95: round1(percentile(r.rtts, 0.95)),
        rttMax: round1(r.rtts.length ? Math.max(...r.rtts) : null),
        jitterP50: round1(percentile(r.ipdv, 0.5))
    };
    return {
        second,
        ...r.counts,
        ...percentiles,
        loadSentKbps: mean(r.loadSentKbps),
        loadReceivedKbps: mean(r.loadReceivedKbps)
    };
}

/**
 * Compresses the seconds `[from, to]` into bins of `size` seconds, for the whole-run view. Counts add
 * up, the load rates average over the seconds that had a report, the max is the bin's max and the other figures are the
 * median of the bin's seconds, so a one-second spike still shows as a max dot.
 */
export function binSeconds(buckets: SecondBucket[], from: number, to: number, size: number): SecondBucket[] {
    const inRange = buckets.filter((b) => b.second >= from && b.second <= to);
    if (size <= 1) return inRange;
    const bins = new Map<number, SecondBucket[]>();
    for (const b of inRange) {
        const start = from + Math.floor((b.second - from) / size) * size;
        const members = bins.get(start);
        if (members) members.push(b);
        else bins.set(start, [b]);
    }
    return [...bins.entries()].map(([start, members]) => {
        const sum = (pick: (b: SecondBucket) => number) => members.reduce((n, b) => n + pick(b), 0);
        const median = (pick: (b: SecondBucket) => number | null) => percentile(nonNull(members.map(pick)), 0.5);
        const maxima = nonNull(members.map((b) => b.rttMax));
        return {
            second: start,
            sent: sum((b) => b.sent),
            echoed: sum((b) => b.echoed),
            lostUp: sum((b) => b.lostUp),
            lostDown: sum((b) => b.lostDown),
            lostUnknown: sum((b) => b.lostUnknown),
            late: sum((b) => b.late),
            rttP50: median((b) => b.rttP50),
            rttP95: median((b) => b.rttP95),
            rttMax: maxima.length ? Math.max(...maxima) : null,
            jitterP50: median((b) => b.jitterP50),
            loadSentKbps: mean(nonNull(members.map((b) => b.loadSentKbps))),
            loadReceivedKbps: mean(nonNull(members.map((b) => b.loadReceivedKbps)))
        };
    });
}

const emptyBucket = (second: number): SecondBucket => ({
    second,
    sent: 0,
    echoed: 0,
    lostUp: 0,
    lostDown: 0,
    lostUnknown: 0,
    late: 0,
    rttP50: null,
    rttP95: null,
    rttMax: null,
    jitterP50: null,
    loadSentKbps: null,
    loadReceivedKbps: null
});

/** Every slot of `size` seconds in `[from, to]`, empty where nothing was measured, so a line breaks there instead of bridging it. */
export function chartSlots(buckets: SecondBucket[], from: number, to: number, size: number): SecondBucket[] {
    const step = Math.max(1, size);
    const bins = new Map(binSeconds(buckets, from, to, step).map((b) => [b.second, b]));
    const out: SecondBucket[] = [];
    for (let s = from; s <= to; s += step) out.push(bins.get(s) ?? emptyBucket(s));
    return out;
}

/** The seconds in `[from, to]` in which no probe was sent: the line was not measured, which is not a good line. */
export function unmeasuredSpans(buckets: SecondBucket[], from: number, to: number): Span[] {
    const measured = new Set(buckets.filter((b) => b.sent > 0).map((b) => b.second));
    const spans: Span[] = [];
    for (let s = from; s <= to; s++) {
        if (measured.has(s)) continue;
        const last = spans[spans.length - 1];
        if (last && last.to === s) last.to = s + 1;
        else spans.push({ from: s, to: s + 1 });
    }
    return spans;
}

/** The windows measured partly in a background tab, as spans of seconds since `originWall`. */
export function backgroundSpans(windows: WindowSummary[], originWall: number): Span[] {
    const spans: Span[] = [];
    for (const w of [...windows].sort((a, b) => a.windowStart - b.windowStart)) {
        if (w.backgroundMs <= 0) continue;
        const from = Math.floor((w.windowStart - originWall) / 1000);
        const to = from + 10;
        const last = spans[spans.length - 1];
        if (last && last.to >= from) last.to = Math.max(last.to, to);
        else spans.push({ from, to });
    }
    return spans;
}

/** The header figures: ping and jitter over the last `seconds` settled seconds, loss over the whole run. */
export function headline(buckets: SecondBucket[], through: number, seconds = 10) {
    const recent = buckets.filter((b) => b.second > through - seconds && b.second <= through);
    const sent = buckets.reduce((n, b) => n + b.sent, 0);
    const lost = buckets.reduce((n, b) => n + b.lostUp + b.lostDown + b.lostUnknown, 0);
    return {
        rttP50: percentile(nonNull(recent.map((b) => b.rttP50)), 0.5),
        jitterP50: percentile(nonNull(recent.map((b) => b.jitterP50)), 0.5),
        lossPercent: sent > 0 ? (lost / sent) * 100 : null
    };
}
